import { useQuery } from "@tanstack/react-query";
import type { SortingState } from "@tanstack/react-table";
import { memo, useMemo, useState } from "react";
import { Link } from "react-router";

import type { ProjectOverviewItemDto } from "@/api/types";
import { ProjectLogo } from "@/components/ProjectLogo/ProjectLogo";
import { Badge } from "@/design-system/Badge";
import { Box } from "@/design-system/Box";
import { Inline } from "@/design-system/Inline";
import { createDashboardColumnHelper } from "@/design-system/Table/helpers";
import { TABLE_COLUMN_SIZES } from "@/design-system/Table/helpers";
import { Table } from "@/design-system/Table/Table";
import { Tooltip } from "@/design-system/Tooltip";
import { isDefined } from "@/helpers/util";
import { TABLE_KEYS, useResizableTable } from "@/hooks/useResizableTable";
import type { AnalyticsRouteParams } from "@/modules/analytics/routes";
import { useSelfAccountQueries } from "@/queries/self-account/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { calculateStats, optionalNumberSort } from "../helpers";
import { BluePill } from "./BluePill";

interface ListViewProps {
  items: ProjectOverviewItemDto[];
  showLiveStats: boolean;
  selectedDate: string;
  isLoading: boolean;
}

export const ListView = memo(function ListView({
  items,
  showLiveStats,
  selectedDate,
  isLoading,
}: ListViewProps): React.ReactNode {
  const [sorting, setSorting] = useState<SortingState>([
    {
      id: "name",
      desc: false,
    },
  ]);

  const selfAccountQueriesa = useSelfAccountQueries();
  const { data: linkedProjects = [] } = useQuery(selfAccountQueriesa.listLinkedProjects());

  const { t } = useTranslation();
  const tableData = useMemo(
    () =>
      items.map((x) => ({
        ...x,
        ...calculateStats(x),
      })),
    [items],
  );

  const canSeeAdoptionOrEngagement = items.some(
    (x) => x.userRoleType !== "caretaker" && x.userRoleType !== "maintenance",
  );
  // Pre-compute view permissions for analytics page per project
  const canViewAnalyticsPageConfig: Record<string, boolean> = useMemo(() => {
    const config: Record<string, boolean> = {};

    linkedProjects.forEach((project) => {
      config[project.slug] =
        project.userRole !== "resident" &&
        project.userRole !== "caretaker" &&
        project.userRole !== "maintenance" &&
        project.userRole !== "servicePartner";
    });

    return config;
  }, [linkedProjects]);

  const columns = useMemo(() => {
    const helper = createDashboardColumnHelper<(typeof tableData)[number]>();

    return [
      helper.accessor("logoUrl", {
        header: "",
        cell: (cell) => (
          <Box padding={{ y: "sm", start: "md" }}>
            <span className="size-control-md">
              <ProjectLogo src={cell.getValue()} />
            </span>
          </Box>
        ),
        size: 56,
        enableSorting: false,
        meta: {
          isVisualIndicator: true,
        },
      }),
      helper.accessor("name", {
        header: t("page.portfolio.table.header.name"),
        cell: (cell) => (
          <Inline gap="sm" align="center">
            <span className="truncate">{cell.getValue()}</span>
            {cell.row.original.activeState === "closed" && (
              <Badge variant="danger">{t("component.project-item.badge.closed")}</Badge>
            )}
            {cell.row.original.activeState === "test" && (
              <Badge variant="special">{t("component.project-item.badge.test")}</Badge>
            )}
            {cell.row.original.activeState === "demo" && (
              <Badge variant="special">{t("component.project-item.badge.demo")}</Badge>
            )}
            {cell.row.original.type === "companyBased" && (
              <Badge variant="info">{t("component.project-item.badge.company-based")}</Badge>
            )}
          </Inline>
        ),
        meta: {
          shouldSimulateRowClick: true,
          renderTruncatedTooltip: (cell) => cell.getValue(),
        },
        size: TABLE_COLUMN_SIZES.LEADING_CONTENT.DEFAULT,
        minSize: TABLE_COLUMN_SIZES.LEADING_CONTENT.MIN,
      }),
      helper.accessor("city", {
        header: t("page.portfolio.table.header.city"),
        cell: (cell) => cell.getValue() || "-",
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
      }),
      helper.accessor("feelingAtHomeRating", {
        header: t("page.portfolio.table.header.fah-rating"),
        cell: (cell) => {
          const value = cell.getValue();
          const stats = showLiveStats ? value.live : value.periodic;

          if (!stats.value) {
            return <span>-</span>;
          }

          const canViewAnalyticsDetails = canViewAnalyticsPageConfig[cell.row.original.slug];
          const content = (
            <>
              <span className="transition-colors group-hover/fah-score-cell:text-text-brand group-hover/fah-score-cell:underline">
                {stats.value}
              </span>
              {isDefined(stats.changePercentage) && <BluePill percentage={stats.changePercentage} />}
            </>
          );

          if (!canViewAnalyticsDetails) {
            return <span className="flex size-full items-center gap-space-xs whitespace-nowrap">{content}</span>;
          }

          return (
            <Tooltip tooltip={t("page.portfolio.table.cell.tooltip.fah-rating")}>
              <Link
                to={`${routes.analytics.overview({
                  slug: cell.row.original.slug,
                })}#fah-section`}
                className="group/fah-score-cell flex size-full items-center gap-space-xs whitespace-nowrap"
              >
                {content}
              </Link>
            </Tooltip>
          );
        },
        sortFn: (a, b) => {
          if (showLiveStats) {
            return optionalNumberSort(
              a.original.feelingAtHomeRating.live?.value,
              b.original.feelingAtHomeRating.live?.value,
            );
          }

          return optionalNumberSort(
            a.original.feelingAtHomeRating.periodic?.value,
            b.original.feelingAtHomeRating.periodic?.value,
          );
        },
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
      }),
      helper.accessor("averageTicketRating", {
        header: t("page.portfolio.table.header.avg-rating"),
        cell: (cell) => {
          const value = cell.getValue();
          const stats = showLiveStats ? value.live : value.periodic;

          if (!stats.value) {
            return <span>-</span>;
          }

          const canViewAnalyticsDetails = canViewAnalyticsPageConfig[cell.row.original.slug];
          const content = (
            <>
              <span className="transition-colors group-hover/ticket-satisfaction-cell:text-text-brand group-hover/ticket-satisfaction-cell:underline">
                {stats.value}
              </span>
              {isDefined(stats.changePercentage) && <BluePill percentage={stats.changePercentage} />}
            </>
          );

          if (!canViewAnalyticsDetails) {
            return <span className="flex size-full items-center gap-space-xs whitespace-nowrap">{content}</span>;
          }

          return (
            <Tooltip tooltip={t("page.portfolio.table.cell.tooltip.avg-rating")}>
              <Link
                to={{
                  pathname: routes.analytics.overview({
                    slug: cell.row.original.slug,
                  }),
                  hash: "tickets-section",
                  search: new URLSearchParams({
                    "tickets-date-range": selectedDate,
                  } satisfies AnalyticsRouteParams["overview"]).toString(),
                }}
                className="group/ticket-satisfaction-cell flex size-full items-center gap-space-xs whitespace-nowrap"
              >
                {content}
              </Link>
            </Tooltip>
          );
        },
        sortFn: (a, b) => {
          if (showLiveStats) {
            return optionalNumberSort(
              a.original.averageTicketRating.live?.value,
              b.original.averageTicketRating.live?.value,
            );
          }

          return optionalNumberSort(
            a.original.averageTicketRating.periodic?.value,
            b.original.averageTicketRating.periodic?.value,
          );
        },
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
      }),
      ...(canSeeAdoptionOrEngagement
        ? [
            helper.accessor("adoptionPercentage", {
              header: t("page.portfolio.table.header.adoption"),
              cell: (cell) => {
                const value = cell.getValue();
                const stats = showLiveStats ? value.live : value.periodic;

                if (!stats.value) {
                  return <span>-</span>;
                }

                const canViewAnalyticsDetails = canViewAnalyticsPageConfig[cell.row.original.slug];
                const content = (
                  <>
                    <span className="transition-colors group-hover/adoption-cell:text-text-brand group-hover/adoption-cell:underline">
                      {stats.value}%
                    </span>
                    {isDefined(stats.changePercentage) && <BluePill percentage={stats.changePercentage} />}
                  </>
                );

                if (!canViewAnalyticsDetails) {
                  return <span className="flex size-full items-center gap-space-xs whitespace-nowrap">{content}</span>;
                }

                return (
                  <Tooltip tooltip={t("page.portfolio.table.cell.tooltip.adoption")}>
                    <Link
                      to={`${routes.analytics.overview({
                        slug: cell.row.original.slug,
                      })}#adoption-section`}
                      className="group/adoption-cell flex size-full items-center gap-space-xs whitespace-nowrap"
                    >
                      {content}
                    </Link>
                  </Tooltip>
                );
              },
              sortFn: (a, b) => {
                if (showLiveStats) {
                  return optionalNumberSort(
                    a.original.adoptionPercentage.live?.value,
                    b.original.adoptionPercentage.live?.value,
                  );
                }

                return optionalNumberSort(
                  a.original.adoptionPercentage.periodic?.value,
                  b.original.adoptionPercentage.periodic?.value,
                );
              },
              size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
              minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
            }),
          ]
        : []),
      ...(canSeeAdoptionOrEngagement
        ? [
            helper.accessor("engagedResidentPercentage", {
              header: t("page.portfolio.table.header.engagement"),
              cell: (cell) => {
                const value = cell.getValue();
                const stats = showLiveStats ? value.live : value.periodic;

                if (!stats.value) {
                  return <span>-</span>;
                }

                const canViewAnalyticsDetails = canViewAnalyticsPageConfig[cell.row.original.slug];
                const content = (
                  <>
                    <span className="transition-colors group-hover/engagement-cell:text-text-brand group-hover/engagement-cell:underline">
                      {stats.value}%
                    </span>
                    {isDefined(stats.changePercentage) && <BluePill percentage={stats.changePercentage} />}
                  </>
                );

                if (!canViewAnalyticsDetails) {
                  return <span className="flex size-full items-center gap-space-xs whitespace-nowrap">{content}</span>;
                }

                return (
                  <Tooltip tooltip={t("page.portfolio.table.cell.tooltip.engagement")}>
                    <Link
                      to={{
                        pathname: routes.analytics.overview({
                          slug: cell.row.original.slug,
                        }),
                        hash: "esg-section",
                        search: new URLSearchParams({
                          "esg-date-range": selectedDate,
                        } satisfies AnalyticsRouteParams["overview"]).toString(),
                      }}
                      className="group/engagement-cell flex size-full items-center gap-space-xs whitespace-nowrap"
                    >
                      {content}
                    </Link>
                  </Tooltip>
                );
              },
              sortFn: (a, b) => {
                if (showLiveStats) {
                  return optionalNumberSort(
                    a.original.engagedResidentPercentage.live?.value,
                    b.original.engagedResidentPercentage.live?.value,
                  );
                }

                return optionalNumberSort(
                  a.original.engagedResidentPercentage.periodic?.value,
                  b.original.engagedResidentPercentage.periodic?.value,
                );
              },
              size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
              minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
            }),
          ]
        : []),
    ];
  }, [canSeeAdoptionOrEngagement, canViewAnalyticsPageConfig, selectedDate, showLiveStats, t]);

  const table = useResizableTable(TABLE_KEYS.PORTFOLIO_OVERVIEW_LEGACY, {
    columns,
    data: tableData,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
  });

  return (
    <Table
      isLoading={isLoading}
      table={table}
      getRowHref={(row) =>
        routes.home.home({
          slug: row.original.slug,
        })
      }
    />
  );
});
